// ABOUTME: Displays pending family member invitations in grid layout
// ABOUTME: Shows invitation details with resend/cancel actions

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip
} from '@mui/material';
import type { PendingInvitation } from './types';

interface PendingInvitationsSectionProps {
  pendingInvitations: PendingInvitation[];
  onResendInvitation: (invitationId: number) => void;
  onCancelInvitation: (invitationId: number, email: string) => void;
}

const PendingInvitationsSection: React.FC<PendingInvitationsSectionProps> = ({
  pendingInvitations,
  onResendInvitation,
  onCancelInvitation
}) => {
  if (pendingInvitations.length === 0) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }} data-testid="pending-invitations-section">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Pending Invitations ({pendingInvitations.length})
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {pendingInvitations.map((invitation) => (
            <Card
              key={invitation.id}
              variant="outlined"
              sx={{ borderColor: 'warning.main', borderStyle: 'dashed' }}
              data-testid={`invitation-card-${invitation.id}`}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, width: 48, height: 48, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
                    {invitation.firstName && invitation.lastName
                      ? invitation.firstName[0] + invitation.lastName[0]
                      : invitation.email[0].toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="div">
                      {invitation.firstName && invitation.lastName
                        ? `${invitation.firstName} ${invitation.lastName}`
                        : invitation.email}
                    </Typography>
                    {invitation.firstName && invitation.lastName && (
                      <Typography variant="body2" color="text.secondary">
                        {invitation.email}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    label={invitation.customRole || (invitation.role === 1 ? 'Owner' : 'Adult')}
                    size="small"
                    color="warning"
                  />
                  <Chip
                    label="Pending"
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Invited by: {invitation.invitedByName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Sent: {new Date(invitation.invitedAt).toLocaleDateString()}
                </Typography>
                {invitation.message && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    &quot;{invitation.message}&quot;
                  </Typography>
                )}
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => onResendInvitation(invitation.id)}
                    data-testid={`resend-invitation-${invitation.id}`}
                  >
                    Resend
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => onCancelInvitation(invitation.id, invitation.email)}
                    data-testid={`cancel-invitation-${invitation.id}`}
                  >
                    Cancel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PendingInvitationsSection;
